// The Boss Ladder (battle design 7.1): an endless climb above the three bosses. Shared by the server's real
// fights, the Bosses page and the sim. Players see each step as its boss and level ("Fenrir · Lv 4,050", ladderName);
// the step number is only an index.
//
// - Step r (1, 2, 3, ...) is Fenrir, Typhon and Hachimata in turn, at level round(3,000 × 1.35^(r−1)). There is
//   no last step.
// - A step opens when the trainer level reaches the step's level.
// - Entry costs one key of that step's boss (the same keys as the boss fights, one market).
// - No under-level rule: every blow carries the level edge (shared/combat.ts levelEdge), (attacker ÷ defender)²
//   both ways, so a creature at 80% of the step's level deals ×0.64 and takes ×1.56. The Bosses page shows a
//   Power estimate (shared/boss-estimate.ts), never exact odds.
// - A win pays coins (killCoins(level) × 100, as a boss win), 1-2 of the boss region's star item, two fragment drops
//   whose chance grows with the step, and the Awakening Shard rolls (shared/awakening.ts ladderShardChance). No legendary rolls, so boss legendaries stay as scarce.
// - The fight is the boss duel (shared/boss-duel.ts): the phase-2 move engine, seeded, so a seed replays exactly.
import { BOSSES, type BossDef } from './species.ts';
import { BOSS_TOKEN, STAR_ITEM } from './items.ts';
import { killCoins } from './xp.ts';
import { bossFighter, duel } from './boss-duel.ts';
import type { Fighter } from './combat.ts';
import { Rng } from './rng.ts';
import { AURA_AWAKENING_SHARD, AWAKENING_SHARD, auraShardChance, ladderShardChance, rollShards } from './awakening.ts';
import type { BossResult } from './protocol.ts';

export const LADDER_BASE_LEVEL = 3000;
export const LADDER_GROWTH = 1.35;
/** Fragments per craft: 10 Crest Shards make a crest (shared/recipes.ts); 10 Tablet Fragments a Rune Tablet (shared/tablets.ts). */
export const FRAGMENTS_PER_CRAFT = 10;
/** Auto-repeat: time between fights (the team heals and re-enters), and the most keys one run may commit. */
export const LADDER_REPEAT_MS = 30_000;
export const LADDER_MAX_COMMIT = 50;
/** Highest step a message may name: about level 10^17, far past anything reachable. */
export const LADDER_MAX_STEP = 120;

export const TABLET_FRAGMENT = 'tablet-fragment';
/** Crest Shard of a boss: ten make that boss's crest. */
export const crestShard = (bossId: string) => `${bossId}-crest-shard`;
/** The crest item of a boss (account-bound, cosmetic). */
export const crestItem = (bossId: string) => `${bossId}-crest`;

export function validStep(r: unknown): r is number {
  return typeof r === 'number' && Number.isInteger(r) && r >= 1 && r <= LADDER_MAX_STEP;
}

/** Level of step r: round(3,000 × 1.35^(r−1)). */
export function stepLevel(r: number): number {
  return Math.round(LADDER_BASE_LEVEL * Math.pow(LADDER_GROWTH, r - 1));
}

/** Trainer level that opens step r: the step's own level. */
export const stepGate = stepLevel;

/** The boss of step r: Fenrir, Typhon, Hachimata, then round again. */
export function stepBoss(r: number): BossDef {
  return BOSSES[(r - 1) % BOSSES.length];
}

/** What a player sees for step r: the boss's name and the step's level, "Fenrir · Lv 4,050". */
export function ladderName(r: number): string {
  return `${stepBoss(r).name} · Lv ${stepLevel(r).toLocaleString('en-US')}`;
}

/** The key step r costs. */
export function stepKey(r: number): string {
  return BOSS_TOKEN[stepBoss(r).id];
}

export function stepOpen(r: number, trainerLevel: number): boolean {
  return trainerLevel >= stepGate(r);
}

/** Highest step open at this trainer level (0 when none is). */
export function highestOpenStep(trainerLevel: number): number {
  let r = 0;
  while (r < LADDER_MAX_STEP && stepOpen(r + 1, trainerLevel)) r++;
  return r;
}

/** Tablet Fragment chance per win: min(25%, 2% + 1% × r). */
export function tabletChance(r: number): number {
  return Math.min(0.25, Math.round((0.02 + 0.01 * r) * 1e4) / 1e4);
}

/** Crest Shard chance per win: min(10%, 0.5% × r). */
export function crestChance(r: number): number {
  return Math.min(0.1, Math.round(0.005 * r * 1e4) / 1e4);
}

/** Coins per win, as a boss win at the step's level. */
export function ladderCoins(r: number): number {
  return killCoins(stepLevel(r)) * 100;
}

/** What a win can pay, with the chance of each, for the step's page. */
export interface StepReward { item: string; qty: [number, number]; chance: number }
export function stepRewards(r: number): StepReward[] {
  const boss = stepBoss(r);
  return [
    { item: STAR_ITEM[boss.region], qty: [1, 2], chance: 1 },
    { item: TABLET_FRAGMENT, qty: [1, 1], chance: tabletChance(r) },
    { item: crestShard(boss.id), qty: [1, 1], chance: crestChance(r) },
    { item: AWAKENING_SHARD, qty: [1, 1], chance: ladderShardChance(r) },
    { item: AURA_AWAKENING_SHARD, qty: [1, 1], chance: auraShardChance(ladderShardChance(r)) },
  ];
}

/** The rewards of one win, rolled on the fight's own rng after the duel. */
export function rollLadderRewards(r: number, rng: Rng): BossResult['rewards'] {
  const boss = stepBoss(r);
  const items: { item: string; qty: number }[] = [{ item: STAR_ITEM[boss.region], qty: rng.int(1, 2) }];
  if (rng.chance(tabletChance(r))) items.push({ item: TABLET_FRAGMENT, qty: 1 });
  if (rng.chance(crestChance(r))) items.push({ item: crestShard(boss.id), qty: 1 });
  items.push(...rollShards(ladderShardChance(r), rng));
  return { coins: ladderCoins(r), items };
}

export interface LadderFight { win: boolean; bossMaxHp: number; rewards: BossResult['rewards'] }

/** One step fight on a seed: the boss duel, then the rewards on the same rng. The same seed and team replay exactly. */
export function ladderFight(team: { id: number; f: Fighter }[], r: number, seed: number, log?: BossResult['log']): LadderFight {
  const rng = new Rng(seed);
  const boss = stepBoss(r);
  const { win, bossMaxHp } = duel(team, bossFighter(boss.id, stepLevel(r)), rng, log);
  return { win, bossMaxHp, rewards: win ? rollLadderRewards(r, rng) : { coins: 0, items: [] } };
}

// ---------- trainer record ----------
export type LadderRecord = { best: number; wins: number; month: string; monthBest: number };

/** UTC month of a time, as YYYY-MM: the key of the monthly "highest step" record. */
export const monthOf = (ms: number) => new Date(ms).toISOString().slice(0, 7);

/** The record after a win at step r. */
export function recordLadderWin(rec: LadderRecord | undefined, r: number, now: number): LadderRecord {
  const month = monthOf(now);
  const monthBest = rec && rec.month === month ? rec.monthBest : 0;
  return { best: Math.max(rec?.best ?? 0, r), wins: (rec?.wins ?? 0) + 1, month, monthBest: Math.max(monthBest, r) };
}
